<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    left
    :close-on-content-click="false"
    max-width="380"
    min-width="340"
    content-class="notification-bell-menu"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        small
        class="ml-1 notification-bell-btn"
        v-bind="attrs"
        v-on="on"
        title="Notifications"
        aria-label="Notifications"
      >
        <v-badge
          :content="unreadCount > 99 ? '99+' : unreadCount"
          :value="unreadCount > 0"
          color="red accent-3"
          overlap
          bordered
        >
          <v-icon size="20">{{
            unreadCount > 0 ? "mdi-bell-ring" : "mdi-bell-outline"
          }}</v-icon>
        </v-badge>
      </v-btn>
    </template>

    <v-card class="notification-bell-card">
      <div class="notification-bell-header">
        <span class="notification-bell-title">Notifications</span>
        <v-btn
          v-if="unreadCount > 0"
          text
          x-small
          class="notification-bell-readall"
          @click="markAllRead"
        >
          Mark all read
        </v-btn>
      </div>
      <v-divider></v-divider>
      <div class="notification-bell-list">
        <div v-if="loading && !items.length" class="notification-bell-empty">
          <v-progress-circular
            indeterminate
            size="18"
            width="2"
          ></v-progress-circular>
        </div>
        <div v-else-if="!items.length" class="notification-bell-empty">
          <v-icon size="28" class="mb-1" style="opacity: 0.4"
            >mdi-bell-sleep-outline</v-icon
          >
          <div>No notifications yet</div>
        </div>
        <div
          v-for="item in items"
          :key="item.id"
          class="notification-bell-item"
          :class="{ 'notification-bell-item--unread': !item.read }"
          role="button"
          tabindex="0"
          :aria-label="item.title + '. Open notification'"
          @click="openNotification(item)"
          @keydown.enter.self="openNotification(item)"
          @keydown.space.self.prevent="openNotification(item)"
        >
          <v-icon small :color="kindColor(item.kind)" class="mr-2 mt-1">
            {{ kindIcon(item.kind) }}
          </v-icon>
          <div class="notification-bell-item-body">
            <div class="notification-bell-item-title">{{ item.title }}</div>
            <div v-if="item.body" class="notification-bell-item-text">
              {{ item.body }}
            </div>
            <div class="notification-bell-item-time">
              {{ timeAgo(item.created_at) }}
            </div>
          </div>
          <v-btn
            icon
            x-small
            class="notification-bell-item-delete"
            title="Dismiss"
            @click.stop="removeNotification(item)"
          >
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
// Self-contained notification bell: polls GET /notifications (60s + on menu
// open) while the user is authenticated. Agents post notifications through
// the same API (the oracle's "notify the user" tool), so anything can show
// up here - clicking an item marks it read and follows its in-app link.
import { mapGetters } from "vuex";
import api from "@shared/services/api";

var POLL_MS = 60000;

export default {
  name: "NotificationBell",
  data() {
    return {
      menuOpen: false,
      loading: false,
      items: [],
      unreadCount: 0,
      pollTimer: null
    };
  },
  computed: {
    ...mapGetters(["isAuthenticated", "currentUser"])
  },
  watch: {
    isAuthenticated: {
      immediate: true,
      handler(val) {
        if (val) {
          this.startPolling();
        } else {
          this.stopPolling();
          this.items = [];
          this.unreadCount = 0;
        }
      }
    },
    menuOpen(open) {
      if (open) this.fetchNotifications();
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    startPolling() {
      this.stopPolling();
      this.fetchNotifications();
      this.pollTimer = setInterval(this.fetchNotifications, POLL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async fetchNotifications() {
      if (!this.isAuthenticated) return;
      this.loading = true;
      try {
        var response = await api.getNotifications({ limit: 30 });
        var data = response.data || {};
        this.items = data.items || [];
        this.unreadCount = data.unread_count || 0;
      } catch (e) {
        // Silent - the bell must never break the toolbar.
      } finally {
        this.loading = false;
      }
    },
    async openNotification(item) {
      if (!item.read) {
        item.read = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        try {
          await api.markNotificationRead(item.id);
        } catch (e) {
          // ignore
        }
      }
      if (item.link) {
        this.menuOpen = false;
        if (this.$route.fullPath !== item.link) {
          this.$router.push(item.link).catch(function () {});
        }
      }
    },
    async markAllRead() {
      this.items.forEach(function (item) {
        item.read = true;
      });
      this.unreadCount = 0;
      try {
        await api.markAllNotificationsRead();
      } catch (e) {
        // ignore
      }
    },
    async removeNotification(item) {
      this.items = this.items.filter(function (i) {
        return i.id !== item.id;
      });
      if (!item.read) this.unreadCount = Math.max(0, this.unreadCount - 1);
      try {
        await api.deleteNotification(item.id);
      } catch (e) {
        // ignore
      }
    },
    kindIcon(kind) {
      if (kind === "success") return "mdi-check-circle-outline";
      if (kind === "warning") return "mdi-alert-outline";
      if (kind === "error") return "mdi-alert-octagon-outline";
      return "mdi-information-outline";
    },
    kindColor(kind) {
      if (kind === "success") return "green lighten-1";
      if (kind === "warning") return "amber darken-1";
      if (kind === "error") return "red accent-2";
      return "light-blue lighten-1";
    },
    timeAgo(iso) {
      if (!iso) return "";
      // created_at is naive UTC from the API - anchor the diff in UTC too.
      var then = new Date(iso.replace(" ", "T") + "Z").getTime();
      if (isNaN(then)) return "";
      var seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
      if (seconds < 60) return "just now";
      var minutes = Math.floor(seconds / 60);
      if (minutes < 60) return minutes + "m ago";
      var hours = Math.floor(minutes / 60);
      if (hours < 24) return hours + "h ago";
      var days = Math.floor(hours / 24);
      return days + "d ago";
    }
  }
};
</script>

<style>
.notification-bell-menu {
  border: 1px solid var(--border-cyan-20) !important;
  border-radius: 10px !important;
}
.notification-bell-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 8px 14px;
}
.notification-bell-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-bright);
}
.notification-bell-readall {
  color: #0284c7 !important;
  text-transform: none !important;
}
.notification-bell-list {
  max-height: 380px;
  overflow-y: auto;
}
.notification-bell-empty {
  padding: 26px 16px;
  text-align: center;
  color: var(--text-secondary-60);
  font-size: 12px;
}
.notification-bell-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 10px 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid rgba(148, 163, 184, 0.08);
  transition: background-color 0.15s;
}
.notification-bell-item:hover {
  background: rgba(56, 189, 248, 0.06);
}
.notification-bell-item--unread {
  background: rgba(56, 189, 248, 0.09);
}
.notification-bell-item-body {
  flex: 1 1 auto;
  min-width: 0;
}
.notification-bell-item-title {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-bright);
  line-height: 1.35;
}
.notification-bell-item--unread .notification-bell-item-title {
  color: #0369a1;
}
.notification-bell-item-text {
  font-size: 11.5px;
  color: var(--text-secondary-60);
  line-height: 1.35;
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.notification-bell-item-time {
  font-size: 10px;
  color: var(--text-secondary-60);
  opacity: 0.8;
  margin-top: 3px;
}
.notification-bell-item-delete {
  opacity: 0;
  transition: opacity 0.15s;
  margin-top: 2px;
}
.notification-bell-item:hover .notification-bell-item-delete {
  opacity: 0.7;
}
</style>
